import { useState, useEffect } from "react";

const formatWithSeparator = (val) => {
  if (!val && val !== 0) return "";
  const parts = val.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

const formatCurrency = (val) => {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
};

const formatPct = (val) => `${(val * 100).toFixed(1)}%`;
const EXITS = [10_000_000, 50_000_000, 100_000_000];

function NumInput({ value, onChange, placeholder }) {
  const [display, setDisplay] = useState(formatWithSeparator(value));

  useEffect(() => {
    setDisplay(formatWithSeparator(value));
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/,/g, "");
    if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
      setDisplay(raw === "" ? "" : formatWithSeparator(raw));
      onChange(parseFloat(raw) || 0);
    }
  };

  const handleBlur = () => {
    setDisplay(formatWithSeparator(value));
  };

  return (
    <input
      className="input-field"
      type="text"
      inputMode="numeric"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder || "0"}
    />
  );
}

function compute(inputs) {
  const { founders, investorShares, optionPool, safes, raiseAmount, preMoneyVal, newOptionPoolPct } = inputs;
  const totalFounderShares = founders.reduce((sum, f) => sum + (f.shares || 0), 0);
  const totalPreShares = totalFounderShares + investorShares + optionPool;

  let safeShares = 0;
  const safeDetails = safes.map((s) => {
    if (!s.amount || !s.cap) return { ...s, shares: 0, conversionPrice: 0 };
    const capPrice = s.cap / totalPreShares;
    const discountPrice = preMoneyVal / totalPreShares / (1 - (s.discount || 0) / 100);
    const conversionPrice = Math.min(capPrice, discountPrice);
    const shares = s.amount / conversionPrice;
    safeShares += shares;
    return { ...s, shares, conversionPrice };
  });

  const postMoneyVal = preMoneyVal + raiseAmount;
  const newOptionPoolShares =
    ((newOptionPoolPct / 100) * (totalPreShares + safeShares)) /
    (1 - newOptionPoolPct / 100);
  const addedOptionShares = Math.max(0, newOptionPoolShares - optionPool);
  const pricePerShare = preMoneyVal / (totalPreShares + safeShares + addedOptionShares);
  const newInvestorShares = raiseAmount / pricePerShare;
  const totalPostShares = totalPreShares + safeShares + addedOptionShares + newInvestorShares;

  const founderOwnership = founders.map((f) => ({
    name: f.name,
    pct: (f.shares || 0) / totalPostShares,
    shares: f.shares || 0,
  }));

  const ownership = {
    founders: totalFounderShares / totalPostShares,
    existingInvestors: investorShares / totalPostShares,
    optionPool: (optionPool + addedOptionShares) / totalPostShares,
    safeHolders: safeShares / totalPostShares,
    newInvestors: newInvestorShares / totalPostShares,
  };

  const exitReturns = EXITS.map((exit) => ({
    exit,
    founders: founderOwnership.map((f) => ({ name: f.name, amount: exit * f.pct })),
    totalFounders: exit * ownership.founders,
    newInvestors: exit * ownership.newInvestors,
  }));

  return { ownership, founderOwnership, exitReturns, pricePerShare, postMoneyVal, safeDetails };
}

const defaultSafe = { amount: 500000, cap: 8000000, discount: 20 };
const founderColors = ["#00E5BE", "#A78BFA", "#34D399", "#F472B6", "#60A5FA"];
const ownershipColors = {
  founders: "#00E5BE",
  existingInvestors: "#6C63FF",
  optionPool: "#FF6B6B",
  safeHolders: "#FFD93D",
  newInvestors: "#4ECDC4",
};
const ownershipLabels = {
  founders: "All Founders",
  existingInvestors: "Existing Investors",
  optionPool: "Option Pool",
  safeHolders: "SAFE Holders",
  newInvestors: "New Investors",
};

export default function App() {
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState({
    founders: [{ name: "Founder 1", shares: 8000000 }],
    investorShares: 1500000,
    optionPool: 500000,
    safes: [{ ...defaultSafe }],
    raiseAmount: 3000000,
    preMoneyVal: 12000000,
    newOptionPoolPct: 10,
  });
  const [result, setResult] = useState(null);
  const [animIn, setAnimIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimIn(true), 100);
  }, []);

  const update = (key, val) => setInputs((p) => ({ ...p, [key]: val }));

  const updateFounder = (i, key, val) => {
    const f = [...inputs.founders];
    f[i] = { ...f[i], [key]: val };
    setInputs((p) => ({ ...p, founders: f }));
  };

  const updateSafe = (i, key, val) => {
    const s = [...inputs.safes];
    s[i] = { ...s[i], [key]: val };
    setInputs((p) => ({ ...p, safes: s }));
  };

  const addSafe = () =>
    setInputs((p) => ({ ...p, safes: [...p.safes, { ...defaultSafe }] }));

  const removeSafe = (i) =>
    setInputs((p) => ({ ...p, safes: p.safes.filter((_, idx) => idx !== i) }));

  const pickCofounderCount = (count) => {
    const founders = Array.from({ length: count }, (_, i) => ({
      name: `Founder ${i + 1}`,
      shares: i === 0 ? 8000000 : 4000000,
    }));
    setInputs((p) => ({ ...p, founders }));
    setStep(1);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#12121C",
        fontFamily: "'DM Mono','Courier New',monospace",
        color: "#E8E8F0",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&family=Syne:wght@700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .fade-up { opacity:0; transform:translateY(24px); transition:all 0.7s cubic-bezier(0.16,1,0.3,1); }
        .fade-up.in { opacity:1; transform:translateY(0); }
        .input-field { background:#1C1C2A; border:1px solid #2E2E42; border-radius:8px; color:#E8E8F0; font-family:'DM Mono',monospace; font-size:14px; padding:10px 14px; width:100%; outline:none; transition:border-color 0.2s; }
        .input-field:focus { border-color:#00E5BE; }
        .input-field::placeholder { color:#3A3A52; }
        .btn-primary { background:#00E5BE; color:#0A0A0F; border:none; border-radius:8px; font-family:'Syne',sans-serif; font-weight:800; font-size:15px; padding:14px 32px; cursor:pointer; letter-spacing:0.5px; transition:all 0.2s; width:100%; }
        .btn-primary:hover { background:#00FFD4; transform:translateY(-1px); }
        .btn-ghost { background:transparent; color:#888; border:1px solid #2E2E42; border-radius:8px; font-family:'DM Mono',monospace; font-size:13px; padding:8px 16px; cursor:pointer; transition:all 0.2s; }
        .btn-ghost:hover { border-color:#00E5BE; color:#00E5BE; }
        .count-btn { background:#1C1C2A; border:2px solid #2E2E42; border-radius:12px; color:#E8E8F0; font-family:'Syne',sans-serif; font-weight:800; font-size:26px; width:76px; height:76px; cursor:pointer; transition:all 0.18s; display:flex; align-items:center; justify-content:center; }
        .count-btn:hover { border-color:#00E5BE; background:#1A2826; color:#00E5BE; transform:translateY(-3px); box-shadow:0 8px 24px #00E5BE22; }
        .card { background:#1C1C2A; border:1px solid #252535; border-radius:12px; padding:20px; }
        .label { font-size:11px; letter-spacing:1.5px; text-transform:uppercase; color:#666; margin-bottom:6px; display:block; }
        .bar-bg { background:#252535; border-radius:4px; height:8px; overflow:hidden; }
        .bar-fill { height:8px; border-radius:4px; transition:width 0.8s cubic-bezier(0.16,1,0.3,1); }
        .safe-card { background:#161622; border:1px solid #252535; border-radius:10px; padding:16px; margin-bottom:12px; }
        .founder-row { background:#161622; border:1px solid #252535; border-radius:10px; padding:14px 16px; margin-bottom:10px; display:grid; grid-template-columns:1fr 1.4fr; gap:12px; align-items:end; }
        .tag { display:inline-block; background:#1C1C2A; border:1px solid #2E2E42; border-radius:4px; font-size:10px; letter-spacing:1px; padding:3px 8px; color:#888; text-transform:uppercase; }
        .exit-card { background:linear-gradient(135deg,#1C1C2A,#161622); border:1px solid #252535; border-radius:12px; padding:20px; text-align:center; transition:border-color 0.2s; }
        .exit-card:hover { border-color:#00E5BE33; }
        .divider { height:1px; background:linear-gradient(90deg,transparent,#2E2E42,transparent); margin:12px 0; }
        ::-webkit-scrollbar { width:6px; }
        ::-webkit-scrollbar-track { background:#12121C; }
        ::-webkit-scrollbar-thumb { background:#2E2E42; border-radius:3px; }
      `}</style>

      {/* ── Header ── */}
      <div
        style={{
          borderBottom: "1px solid #252535",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          background: "#12121CF2",
          backdropFilter: "blur(12px)",
          zIndex: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "linear-gradient(135deg,#00E5BE,#6C63FF)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
            }}
          >
            ◈
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Syne',sans-serif",
                fontWeight: 800,
                fontSize: 15,
                letterSpacing: "0.5px",
              }}
            >
              CAP TABLE SCENARIO
            </div>
            <div style={{ fontSize: 10, color: "#555", letterSpacing: "1px" }}>
              BY UNICORN CFO
            </div>
          </div>
        </div>
        <a
          href="https://unicorncfo.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 11,
            color: "#00E5BE",
            textDecoration: "none",
            letterSpacing: "1px",
          }}
        >
          UNICORNCFO.COM →
        </a>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 20px" }}>

        {/* ── STEP 0: Landing ── */}
        {step === 0 && (
          <div className={`fade-up ${animIn ? "in" : ""}`}>
            <div style={{ textAlign: "center", padding: "40px 0 48px" }}>
              <div className="tag" style={{ marginBottom: 20 }}>
                Free Tool · No Signup Required
              </div>
              <h1
                style={{
                  fontFamily: "'Syne',sans-serif",
                  fontWeight: 800,
                  fontSize: "clamp(32px,6vw,52px)",
                  lineHeight: 1.1,
                  marginBottom: 20,
                  background: "linear-gradient(135deg,#E8E8F0 40%,#00E5BE)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                See Exactly What
                <br />
                Your Round Costs You
              </h1>
              <p
                style={{
                  color: "#888",
                  fontSize: 15,
                  lineHeight: 1.7,
                  maxWidth: 480,
                  margin: "0 auto 32px",
                }}
              >
                Model your SAFEs, convertible notes, option pool top-up, and
                new round — and see your real ownership and exit economics
                before you sign anything.
              </p>
              <button
                className="btn-primary"
                style={{ maxWidth: 280, margin: "0 auto" }}
                onClick={() => setStep("founders")}
              >
                Start Modeling →
              </button>
              <div
                style={{
                  marginTop: 24,
                  display: "flex",
                  gap: 24,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {["SAFE Conversion", "Dilution Waterfall", "Exit Scenarios"].map(
                  (t) => (
                    <div
                      key={t}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        color: "#555",
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: "#00E5BE" }}>✓</span> {t}
                    </div>
                  )
                )}
              </div>
            </div>

            <div
              style={{
                background: "#1C1C2A",
                border: "1px solid #FF6B6B33",
                borderRadius: 10,
                padding: "16px 20px",
                display: "flex",
                gap: 14,
                alignItems: "flex-start",
              }}
            >
              <span style={{ fontSize: 20 }}>⚠️</span>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#FF6B6B",
                    fontWeight: 500,
                    marginBottom: 4,
                  }}
                >
                  Most founders don't see this until it's too late
                </div>
                <div style={{ fontSize: 12, color: "#666", lineHeight: 1.6 }}>
                  Between SAFEs converting, option pool top-ups, and new round
                  dilution — the average seed-stage founder loses 15–25% more
                  ownership than they expected. Model it before you commit.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP founders: Co-founder count ── */}
        {step === "founders" && (
          <div className="fade-up in" style={{ textAlign: "center", padding: "32px 0" }}>
            <div className="tag" style={{ marginBottom: 20 }}>Step 1 of 2</div>
            <h2
              style={{
                fontFamily: "'Syne',sans-serif",
                fontWeight: 800,
                fontSize: 28,
                marginBottom: 12,
              }}
            >
              How many co-founders?
            </h2>
            <p
              style={{
                color: "#666",
                fontSize: 13,
                maxWidth: 360,
                margin: "0 auto 40px",
              }}
            >
              Include everyone who holds founder shares — including yourself.
            </p>
            <div
              style={{
                display: "flex",
                gap: 16,
                justifyContent: "center",
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  className="count-btn"
                  onClick={() => pickCofounderCount(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#3A3A52", marginBottom: 32 }}>
              tap to select
            </div>
            <button
              className="btn-ghost"
              onClick={() => setStep(0)}
              style={{ border: "none" }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* ── STEP 1: Input form ── */}
        {step === 1 && (
          <div className="fade-up in">
            <div style={{ marginBottom: 28 }}>
              <div className="tag" style={{ marginBottom: 12 }}>Step 2 of 2</div>
              <h2
                style={{
                  fontFamily: "'Syne',sans-serif",
                  fontWeight: 800,
                  fontSize: 26,
                  marginBottom: 8,
                }}
              >
                Your Cap Table
              </h2>
              <p style={{ color: "#666", fontSize: 13 }}>
                Approximate numbers are fine. This is scenario modeling, not
                accounting.
              </p>
            </div>

            {/* Founders */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#00E5BE",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  marginBottom: 16,
                }}
              >
                Founder Shares
              </div>
              {inputs.founders.map((f, i) => (
                <div className="founder-row" key={i}>
                  <div>
                    <label className="label">Name</label>
                    <input
                      className="input-field"
                      type="text"
                      value={f.name}
                      onChange={(e) => updateFounder(i, "name", e.target.value)}
                      placeholder={`Founder ${i + 1}`}
                    />
                  </div>
                  <div>
                    <label className="label">Shares</label>
                    <NumInput
                      value={f.shares}
                      onChange={(v) => updateFounder(i, "shares", v)}
                      placeholder="8,000,000"
                    />
                  </div>
                </div>
              ))}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginTop: 4,
                }}
              >
                <div>
                  <label className="label">Existing Investor Shares</label>
                  <NumInput
                    value={inputs.investorShares}
                    onChange={(v) => update("investorShares", v)}
                    placeholder="1,500,000"
                  />
                </div>
                <div>
                  <label className="label">Option Pool Shares</label>
                  <NumInput
                    value={inputs.optionPool}
                    onChange={(v) => update("optionPool", v)}
                    placeholder="500,000"
                  />
                </div>
              </div>
            </div>

            {/* SAFEs */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#FFD93D",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Outstanding SAFEs / Notes
                </div>
                <button
                  className="btn-ghost"
                  onClick={addSafe}
                  style={{ fontSize: 12 }}
                >
                  + Add SAFE
                </button>
              </div>
              {inputs.safes.length === 0 && (
                <div
                  style={{
                    color: "#3A3A52",
                    fontSize: 13,
                    textAlign: "center",
                    padding: "16px 0",
                  }}
                >
                  No SAFEs — click "Add SAFE" if you have any
                </div>
              )}
              {inputs.safes.map((s, i) => (
                <div className="safe-card" key={i}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ fontSize: 12, color: "#888" }}>SAFE #{i + 1}</span>
                    <button
                      onClick={() => removeSafe(i)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#FF6B6B",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      ✕ Remove
                    </button>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 10,
                    }}
                  >
                    <div>
                      <label className="label">Amount ($)</label>
                      <NumInput
                        value={s.amount}
                        onChange={(v) => updateSafe(i, "amount", v)}
                        placeholder="500,000"
                      />
                    </div>
                    <div>
                      <label className="label">Valuation Cap ($)</label>
                      <NumInput
                        value={s.cap}
                        onChange={(v) => updateSafe(i, "cap", v)}
                        placeholder="8,000,000"
                      />
                    </div>
                    <div>
                      <label className="label">Discount (%)</label>
                      <NumInput
                        value={s.discount}
                        onChange={(v) => updateSafe(i, "discount", v)}
                        placeholder="20"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* New Round */}
            <div className="card" style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#6C63FF",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  marginBottom: 16,
                }}
              >
                New Round Parameters
              </div>
              <div
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
              >
                <div>
                  <label className="label">Raise Amount ($)</label>
                  <NumInput
                    value={inputs.raiseAmount}
                    onChange={(v) => update("raiseAmount", v)}
                    placeholder="3,000,000"
                  />
                </div>
                <div>
                  <label className="label">Pre-Money Valuation ($)</label>
                  <NumInput
                    value={inputs.preMoneyVal}
                    onChange={(v) => update("preMoneyVal", v)}
                    placeholder="12,000,000"
                  />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="label">New Option Pool Target (%)</label>
                  <NumInput
                    value={inputs.newOptionPoolPct}
                    onChange={(v) => update("newOptionPoolPct", v)}
                    placeholder="10"
                  />
                  <div style={{ fontSize: 11, color: "#444", marginTop: 4 }}>
                    Investors typically require 10–15% post-money option pool
                  </div>
                </div>
              </div>
            </div>

            <button
              className="btn-primary"
              onClick={() => {
                setResult(compute(inputs));
                setStep(2);
              }}
            >
              Calculate Scenarios →
            </button>
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <button
                className="btn-ghost"
                onClick={() => setStep("founders")}
                style={{ border: "none" }}
              >
                ← Back
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Results ── */}
        {step === 2 && result && (
          <div className="fade-up in">
            <div style={{ marginBottom: 28 }}>
              <div className="tag" style={{ marginBottom: 12 }}>
                Post-Round Analysis
              </div>
              <h2
                style={{
                  fontFamily: "'Syne',sans-serif",
                  fontWeight: 800,
                  fontSize: 26,
                  marginBottom: 4,
                }}
              >
                Your Scenarios
              </h2>
              <div style={{ color: "#555", fontSize: 13 }}>
                Post-money:{" "}
                <span style={{ color: "#00E5BE" }}>
                  {formatCurrency(result.postMoneyVal)}
                </span>
                &nbsp;·&nbsp; Price/share:{" "}
                <span style={{ color: "#00E5BE" }}>
                  ${result.pricePerShare.toFixed(4)}
                </span>
              </div>
            </div>

            {/* Ownership bars */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#00E5BE",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  marginBottom: 20,
                }}
              >
                Post-Round Ownership
              </div>

              {result.founderOwnership.map((f, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ fontSize: 13, color: "#E8E8F0" }}>
                      {f.name}
                      <span
                        style={{
                          color: founderColors[i % founderColors.length],
                          marginLeft: 8,
                          fontSize: 11,
                        }}
                      >
                        ● FOUNDER
                      </span>
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        color: founderColors[i % founderColors.length],
                        fontWeight: 500,
                      }}
                    >
                      {formatPct(f.pct)}
                    </span>
                  </div>
                  <div className="bar-bg">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${f.pct * 100}%`,
                        background: founderColors[i % founderColors.length],
                      }}
                    />
                  </div>
                </div>
              ))}

              <div style={{ height: 1, background: "#252535", margin: "16px 0" }} />

              {Object.entries(result.ownership)
                .filter(([k]) => k !== "founders")
                .map(([key, pct]) =>
                  pct > 0.001 ? (
                    <div key={key} style={{ marginBottom: 14 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 6,
                        }}
                      >
                        <span style={{ fontSize: 13, color: "#888" }}>
                          {ownershipLabels[key]}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            color: ownershipColors[key],
                            fontWeight: 500,
                          }}
                        >
                          {formatPct(pct)}
                        </span>
                      </div>
                      <div className="bar-bg">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${pct * 100}%`,
                            background: ownershipColors[key],
                          }}
                        />
                      </div>
                    </div>
                  ) : null
                )}
            </div>

            {/* SAFE conversion details */}
            {result.safeDetails.filter((s) => s.shares > 0).length > 0 && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: "#FFD93D",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    marginBottom: 16,
                  }}
                >
                  SAFE Conversion Details
                </div>
                {result.safeDetails
                  .filter((s) => s.shares > 0)
                  .map((s, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "10px 0",
                        borderBottom:
                          i < result.safeDetails.length - 1
                            ? "1px solid #252535"
                            : "none",
                      }}
                    >
                      <span style={{ fontSize: 13, color: "#888" }}>
                        SAFE #{i + 1} · {formatCurrency(s.amount)}
                      </span>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, color: "#FFD93D" }}>
                          ${s.conversionPrice.toFixed(4)}/share
                        </div>
                        <div style={{ fontSize: 11, color: "#555" }}>
                          {Math.round(s.shares).toLocaleString()} shares
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Exit scenarios */}
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#6C63FF",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  marginBottom: 16,
                }}
              >
                Founder Exit Returns
              </div>
              <div
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}
              >
                {result.exitReturns.map((e) => (
                  <div className="exit-card" key={e.exit}>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#555",
                        letterSpacing: "1px",
                        marginBottom: 8,
                      }}
                    >
                      {formatCurrency(e.exit)} EXIT
                    </div>
                    <div
                      style={{
                        fontFamily: "'Syne',sans-serif",
                        fontWeight: 800,
                        fontSize: 20,
                        color: "#00E5BE",
                        marginBottom: 4,
                      }}
                    >
                      {formatCurrency(e.totalFounders)}
                    </div>
                    <div style={{ fontSize: 10, color: "#555", marginBottom: 8 }}>
                      total founders
                    </div>
                    {e.founders.length > 1 && (
                      <div
                        style={{ borderTop: "1px solid #252535", paddingTop: 8 }}
                      >
                        {e.founders.map((f, fi) => (
                          <div
                            key={fi}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 11,
                              marginBottom: 3,
                            }}
                          >
                            <span style={{ color: "#666" }}>{f.name}</span>
                            <span
                              style={{
                                color: founderColors[fi % founderColors.length],
                              }}
                            >
                              {formatCurrency(f.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="divider" />
                    <div style={{ fontSize: 11, color: "#555" }}>
                      New VCs: {formatCurrency(e.newInvestors)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Key insight */}
            <div
              style={{
                background: "linear-gradient(135deg,#1C1C2A,#161622)",
                border: "1px solid #00E5BE33",
                borderRadius: 12,
                padding: "20px",
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "#00E5BE",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Key Insight
              </div>
              <div style={{ fontSize: 14, color: "#AAA", lineHeight: 1.7 }}>
                After this round, founders collectively own{" "}
                <strong style={{ color: "#00E5BE" }}>
                  {formatPct(result.ownership.founders)}
                </strong>{" "}
                of the company. Every 10% additional dilution in future rounds
                reduces your {formatCurrency(EXITS[2])} exit by{" "}
                <strong style={{ color: "#FF6B6B" }}>
                  {formatCurrency(EXITS[2] * 0.1)}
                </strong>
                . Model your next round before it happens.
              </div>
            </div>

            {/* CTA */}
            <div
              style={{
                background: "#1C1C2A",
                border: "1px solid #2E2E42",
                borderRadius: 12,
                padding: "24px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: "'Syne',sans-serif",
                  fontWeight: 800,
                  fontSize: 18,
                  marginBottom: 8,
                }}
              >
                Want a full fundraising strategy around these numbers?
              </div>
              <div
                style={{ color: "#666", fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}
              >
                Unicorn CFO helps seed-to-Series A founders build financial
                infrastructure, model scenarios, and walk into investor meetings
                prepared.
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <a
                  href="https://calendly.com/ahmet-unicorncfo/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: "#00E5BE",
                    color: "#0A0A0F",
                    borderRadius: 8,
                    padding: "12px 24px",
                    textDecoration: "none",
                    fontFamily: "'Syne',sans-serif",
                    fontWeight: 800,
                    fontSize: 14,
                  }}
                >
                  Book a Free 30-Min Call →
                </a>
                <button
                  className="btn-ghost"
                  onClick={() => {
                    setStep(1);
                    setResult(null);
                  }}
                >
                  ← Adjust Inputs
                </button>
              </div>
            </div>

            <div
              style={{
                textAlign: "center",
                marginTop: 16,
                fontSize: 11,
                color: "#333",
              }}
            >
              This tool provides estimates for scenario planning purposes only.
              Not financial or legal advice.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
